import type { AssignStaffToPracticeRef, Staff, StaffSpeciality } from '@/types/settings-api';

/**
 * Spec-frozen blank staff for create-mode initialisation. Mirrors Flutter
 * `staff_model.dart` defaults — `isActive: true`, empty arrays for
 * collections, nulls for ids/photo/last-login.
 */
export function emptyStaff(): Staff {
  return {
    // PROD-1817: PM's phantom `providerId` corrected to `assignDoctor`, the
    // real wire field. PM's own type comment says to rename it; PA already did
    // (PROD-1206), so the port keeps PA's correction rather than importing the bug.
    assignDoctor: null,
    staffSetupId: null,
    firstName: '',
    lastName: '',
    role: null,
    emailId: '',
    mobileNumber: '',
    isActive: true,
    assignStaffToPractice: [],
    specialities: [],
    assignStaffToSpecialities: [],
    isLastLogin: null,
    photoURL: null,
  };
}

/**
 * Critical invariant: `specialities[]` and `assignStaffToSpecialities[]` are
 * a parallel pair — backend joins via both. When mutating either, mutate both
 * together to keep them in sync. This helper enforces that.
 */
export function setStaffSpecialities(
  staff: Staff,
  next: StaffSpeciality[],
): Staff {
  return {
    ...staff,
    specialities: next,
    assignStaffToSpecialities: next.map((s) => s.specialitySkillId),
  };
}

/**
 * Toggle a practice assignment. Flutter parity (`add_edit_staff_widget.dart`):
 * the row stays in the array with `isAssign: false` rather than being
 * removed — backend interprets removal as "no change" rather than "unassign".
 */
export function toggleStaffPracticeAssignment(
  staff: Staff,
  practiceId: string,
  isAssign: boolean,
): Staff {
  const existing = staff.assignStaffToPractice.find(
    (a) => a.practiceId === practiceId,
  );
  if (existing) {
    return {
      ...staff,
      assignStaffToPractice: staff.assignStaffToPractice.map((a) =>
        a.practiceId === practiceId ? { ...a, isAssign } : a,
      ),
    };
  }
  const next: AssignStaffToPracticeRef = { practiceId, isAssign };
  return {
    ...staff,
    assignStaffToPractice: [...staff.assignStaffToPractice, next],
  };
}

/**
 * Equality check used to drive the Save-button dirty state. Compares the
 * server-source-of-truth `source` to the user-edited `draft`. Cheap
 * stringify is fine — the staff record is small (<2KB) and equality runs
 * once per render.
 */
export function isStaffDirty(source: Staff, draft: Staff): boolean {
  return JSON.stringify(source) !== JSON.stringify(draft);
}

/**
 * Pre-save normaliser. Trims text fields, scrubs server-controlled fields.
 * Used by `StaffEditPage` immediately before dispatching the create/update
 * mutation.
 *
 * Per-field PHI/wire audit (@samer review on PR #64):
 *
 * Server-controlled, SCRUBBED to null on save (defense in depth — backend
 * also ignores these on writes, but we shouldn't be the ones echoing them):
 *   - `isLastLogin` — last successful login timestamp.
 *
 * Trimmed text fields that ship to the server:
 *   - `firstName`, `lastName`, `emailId`, `mobileNumber`.
 *
 * Passed through verbatim (caller-owned, validated upstream):
 *   - `providerId`        — Doctor link, nullable, set by separate flow.
 *                          (Wire field is actually `assignDoctor` — see
 *                          Staff type note; harmless passthrough for now.)
 *   - `staffSetupId`      — backend PK (null on create, populated on edit).
 *   - `role`              — integer enum, validated by RoleSelect.
 *   - `isActive`          — boolean, controlled by ACCOUNT STATUS toggle.
 *   - `assignStaffToPractice` — `{practiceId, isAssign}[]`, mutations
 *                              flow through `toggleStaffPracticeAssignment`.
 *   - `specialities`          — full SNOMED code objects. v1 UI is
 *                              read-only, so values can only originate from
 *                              a prior server response (no raw user codes).
 *                              Once the SNOMED typeahead lands, the lookup
 *                              endpoint validates entries before they enter
 *                              this array.
 *   - `assignStaffToSpecialities` — parallel id array; kept in sync via
 *                                  `setStaffSpecialities`.
 *   - `photoURL`              — Azure blob URL, set by upload flow.
 */
export function normaliseStaffForSave(staff: Staff): Staff {
  return {
    ...staff,
    firstName: staff.firstName.trim(),
    lastName: staff.lastName.trim(),
    emailId: staff.emailId.trim(),
    mobileNumber: staff.mobileNumber.trim(),
    // Server-controlled — never echo back on save.
    isLastLogin: null,
  };
}
